import errorMap from '@gap/backend/util/validations/validation-error-map'
import { z } from 'zod'
import { userJobTitleEnumSchema } from './UserJobTitleDto'
import validationConstants from '@gap/utils/validation/validation-constants'
import NumberRange from '@gap/utils/NumberRange'
import { normalisedEmailValidation } from '@gap/backend/util/validations/validation-email'
import { UserTrialDataDto } from './UserTrialDataDto'

export type UserOnboardingSteps = NumberRange<1, 9>

export const gettingStartedStepEnum = ['setupAccount', 'connectCalendar', 'runFirstMeeting', 'transcribeMeeting', 'sendPreWork', 'inviteTeamMember'] as const
export const gettingStartedStep = z.enum(gettingStartedStepEnum)
export type GettingStartedStep = z.output<typeof gettingStartedStep>

/**
 * How far ahead the Meetings-list "upcoming" section shows by default. Persisted per-user on the
 * UserEntity (DynamoDB) and set from the My Profile → Calendar tab. Single source of truth for the
 * tRPC contract shared by the calendar preferences store and the meetings list. Default `next-7`.
 */
export const showUpcomingDaysEnum = ['today', 'next-3', 'next-7', 'next-14'] as const
export const showUpcomingDaysSchema = z.enum(showUpcomingDaysEnum)
export type ShowUpcomingDays = z.output<typeof showUpcomingDaysSchema>
export const DEFAULT_SHOW_UPCOMING_DAYS: ShowUpcomingDays = 'next-7'

/**
 * Whether Scribe auto-joins the user's linked calendar events. Persisted per-user on the
 * UserEntity (DynamoDB) and set from the My Profile → Calendar tab. Defaults off, so a user who
 * has never opted in gets no scheduled bots.
 */
export const DEFAULT_AUTO_JOIN_ENABLED = false

const UserEntityDtoSchema = z.object({
   id: z.string({ errorMap }),
   email: normalisedEmailValidation,
   firstName: z.string({ errorMap }).max(validationConstants.userEntity.maxFirstNameLength),
   lastName: z.string({ errorMap }).max(validationConstants.userEntity.maxLastNameLength),
   lastUpdated: z.string({ errorMap }),
   cognitoUsername: z.string({ errorMap }).optional(),
   inviteSentOn: z.string({ errorMap }).optional(),
   inviteAcceptedOn: z.string({ errorMap }).optional(),
   jobTitle: userJobTitleEnumSchema.optional(),
   trialData: UserTrialDataDto.createSchema.optional(),
   onboarding: z.object({
      hasViewedWelcome: z.boolean({ errorMap }),
      hasViewedTacticAnnouncement: z.boolean({ errorMap }),
      showCheckList: z.boolean(),
      completedSteps: z.array(z.number().min(1).max(9)) as z.ZodType<UserOnboardingSteps[]>,
      hasSeenMeetingGuidedTour: z.boolean({ errorMap }).optional(),
      hasConsentedToTranscription: z.boolean({ errorMap }).optional(),
      gettingStartedSteps: z.array(gettingStartedStep),
      gettingStartedSeenSteps: z.array(gettingStartedStep).optional(),
      gettingStartedCollapsed: z.boolean().optional(),
      gettingStartedCompletedAt: z.string().optional(),
   }),
   calendarPreferences: z
      .object({
         showUpcomingDays: showUpcomingDaysSchema,
         // Defaulted rather than optional so rows written before auto-join existed read back as off.
         autoJoinEnabled: z.boolean({ errorMap }).default(DEFAULT_AUTO_JOIN_ENABLED),
      })
      .optional(),
   isTrialist: z.boolean({ errorMap }).optional(),
   circleId: z.string({ errorMap }).optional(),
   circleLastAttemptAt: z.string({ errorMap }).optional(),
   circleLastError: z.string({ errorMap }).optional(),
})

export default UserEntityDtoSchema
